/**
 * 보안 메모 작성/편집 폼
 * 리치 텍스트 에디터와 파일 첨부 기능
 */

import React, { useState, useEffect } from 'react';
import { useSecureNotes } from '../../contexts/SecureNotesContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePin } from '../../contexts/PinContext';
import RichTextEditor from './RichTextEditor';
import FileAttachment from './FileAttachment';
import PinModal from './PinModal';
import { uploadNoteFiles, deleteNoteFile } from '../../services/storageService';
import { Modal, Button } from '../UI';
import {
  Save,
  X,
  Lock,
  Tag,
  Folder,
  Star,
  Archive
} from 'lucide-react';
import DOMPurify from 'dompurify';
import './SecureNotes.css';

function SecureNoteForm({ note, currentFolderId, onClose, onSave }) {
  const { createNote, updateNote } = useSecureNotes();
  const { user } = useAuth();
  const { pinSettings, checkUnlockStatus, verifyPin } = usePin();
  
  // 폼 상태
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [plainTextContent, setPlainTextContent] = useState('');
  const [category, setCategory] = useState('');
  const [folderId, setFolderId] = useState(currentFolderId || 'all-notes');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [isPinProtected, setIsPinProtected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  
  // 편집 모드시 데이터 로드 (초기 로딩 시에만 실행)
  useEffect(() => {
    if (note) {
      setTitle(note.title || '');
      setContent(note.content || '');
      setPlainTextContent(note.plainTextContent || '');
      setFolderId(note.folderId || currentFolderId || 'all-notes');
      setCategory(note.category || '');
      setTags(note.tags || []);
      setAttachments(note.attachments || []);
      setIsFavorite(note.isFavorite || false);
      setIsArchived(note.isArchived || false);
      setIsPinProtected(note.isPinProtected || false);
    }
  }, [note?.id, currentFolderId]); // note 객체 참조 대신 note.id로 변경
  
  // 에디터 내용 변경 처리
  const handleContentChange = (html) => {
    // HTML 정제
    const sanitized = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
        'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote',
        'a', 'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'mark', 'span'
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'target', 'rel', 'class', 'style', 'data-type', 'data-checked']
    });

    setContent(sanitized);
    // HTML에서 텍스트만 추출
    const text = sanitized.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    setPlainTextContent(text);
  };
  
  // 태그 추가
  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };
  
  // 태그 제거
  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };
  
  // 태그 입력 키 처리
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };
  
  // 파일 첨부 처리
  const handleFileAttach = async (files) => {
    if (!user) {
      setError('사용자 인증이 필요합니다.');
      return;
    }

    setLoading(true);
    try {
      // 파일을 Firebase Storage에 업로드
      const uploadedFiles = await uploadNoteFiles(
        files.map(f => f.file), // File 객체만 추출
        user.uid,
        note?.id || null // 편집 모드가 아니면 임시 업로드
      );

      // 업로드된 파일 정보를 첨부파일 목록에 추가
      const newAttachments = uploadedFiles.map(uploaded => ({
        id: uploaded.id,
        name: uploaded.name,
        type: uploaded.type,
        size: uploaded.size,
        url: uploaded.url,
        path: uploaded.path,
        uploadedAt: uploaded.uploadedAt
      }));

      setAttachments([...attachments, ...newAttachments]);
    } catch (err) {
      console.error('File upload error:', err);
      setError('파일 업로드에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  // 파일 제거
  const handleFileRemove = async (fileId) => {
    const fileToRemove = attachments.find(file => file.id === fileId);
    
    if (fileToRemove && fileToRemove.path) {
      try {
        // Storage에서 파일 삭제
        await deleteNoteFile(fileToRemove.path);
      } catch (err) {
        console.error('Error deleting file from storage:', err);
        // Storage 삭제 실패해도 UI에서는 제거
      }
    }
    
    setAttachments(attachments.filter(file => file.id !== fileId));
  };
  
  // 저장
  const handleSave = async (verifiedPin = null) => {
    if (!title.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }

    if (!content.trim()) {
      setError('내용을 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const noteData = {
        title: title.trim(),
        content,
        plainTextContent,
        category,
        folderId,
        tags,
        attachments,
        isFavorite,
        isArchived,
        isPinProtected: isPinProtected || !!verifiedPin
      };

      if (note) {
        // 편집 모드: PIN이 필요한 경우 verifiedPin 전달
        await updateNote(note.id, noteData, verifiedPin);
      } else {
        await createNote(noteData, verifiedPin);
      }

      onClose();
    } catch (err) {
      console.error('Error saving note:', err);
      setError('메모 저장에 실패했습니다.');
    } finally {
      setLoading(false);
      setPendingSave(false);
    }
  };
  
  // PIN 모달 제출 핸들러
  const handlePinSubmit = async (enteredPin) => {
    const pinString = enteredPin.join('');

    // PIN 검증
    const isValid = await verifyPin(pinString);
    if (!isValid) {
      return { success: false, error: '잘못된 PIN입니다.' };
    }

    // PIN 검증 성공 - 모달 닫고 저장 진행
    setShowPinModal(false);

    // PIN을 전달하여 저장 (unlock 상태 체크 우회)
    await handleSave(pinString);

    return { success: true };
  };

  // 저장 버튼 클릭
  const handleSaveClick = () => {
    // Master PIN이 없는 경우 경고
    if (isPinProtected && !pinSettings.hasMasterPin) {
      alert('PIN 보호를 사용하려면 먼저 설정에서 마스터 PIN을 설정하세요.');
      return;
    }

    // 편집 모드에서 PIN 보호된 메모를 저장할 때 PIN 확인
    if (note && note.isPinProtected) {
      // 이미 언락된 상태인지 확인
      const isUnlocked = checkUnlockStatus();

      if (!isUnlocked) {
        // 언락되지 않은 경우 PIN 모달 표시
        setPendingSave(true);
        setShowPinModal(true);
        return;
      }
      // 언락된 상태면 PIN 없이 저장 가능
    }

    // PIN 보호가 체크되어도 개별 PIN 설정 모달을 띄우지 않고 바로 저장
    handleSave();
  };
  
  // 카테고리 목록
  const categories = [
    { value: '', label: '카테고리 선택' },
    { value: 'personal', label: '개인' },
    { value: 'work', label: '업무' },
    { value: 'finance', label: '금융' },
    { value: 'health', label: '건강' },
    { value: 'other', label: '기타' }
  ];
  
  return (
    <>
      {/* PIN 입력 모달 */}
      <PinModal
        isOpen={showPinModal}
        onClose={() => {
          setShowPinModal(false);
          setPendingSave(false);
        }}
        onSubmit={handlePinSubmit}
        title="메모 저장을 위한 PIN 입력"
        mode="verify"
      />

      <Modal
        isOpen={true}
        onClose={onClose}
        size="xl"
        title={note ? '메모 편집' : '새 메모 작성'}
        showCloseButton={!loading}
        className="secure-note-form-modal"
      >
        <div className="secure-note-form">
          {/* 제목 */}
          <div className="form-group">
            <input
              type="text"
              className="form-input note-title-input"
              placeholder="제목을 입력하세요"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              autoFocus
            />
          </div>
          
          {/* 카테고리 및 옵션 */}
          <div className="form-row">
            <div className="form-group">
              <label>
                <Folder size={16} />
                카테고리
              </label>
              <select
                className="form-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-options">
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={isPinProtected}
                  onChange={(e) => setIsPinProtected(e.target.checked)}
                />
                <Lock size={16} />
                PIN 보호
              </label>
              
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={isFavorite}
                  onChange={(e) => setIsFavorite(e.target.checked)}
                />
                <Star size={16} />
                즐겨찾기
              </label>
              
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={isArchived}
                  onChange={(e) => setIsArchived(e.target.checked)}
                />
                <Archive size={16} />
                아카이브
              </label>
            </div>
          </div>
          
          {/* 태그 */}
          <div className="form-group">
            <label>
              <Tag size={16} />
              태그
            </label>
            <div className="tags-input">
              <div className="tags-list">
                {tags.map((tag, index) => (
                  <span key={index} className="tag-item">
                    {tag}
                    <button
                      type="button"
                      className="tag-remove"
                      onClick={() => handleRemoveTag(tag)}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                className="tag-input"
                placeholder="태그 입력 후 Enter"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                maxLength={20}
              />
            </div>
          </div>
          
          {/* 에디터 */}
          <div className="form-group">
            <label>내용</label>
            <RichTextEditor
              content={content}
              onChange={handleContentChange}
              placeholder="메모 내용을 작성하세요..."
              minHeight={400}
              theme="light"
              features={{
                basic: true,
                heading: true,
                list: true,
                taskList: true,
                blockquote: true,
                codeBlock: false,
                codeHighlight: true,
                link: true,
                image: true,
                table: true,
                textColor: true,
                highlight: true,
                align: true,
                indent: true,
              }}
            />
          </div>
          
          {/* 파일 첨부 */}
          <div className="form-group">
            <label>첨부 파일</label>
            <FileAttachment
              attachments={attachments}
              onAttach={handleFileAttach}
              onRemove={handleFileRemove}
            />
          </div>
          
          {/* 에러 메시지 */}
          {error && (
            <div className="form-error">
              {error}
            </div>
          )}
          
          {/* 액션 버튼 */}
          <div className="form-actions">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              취소
            </Button>
            <Button
              variant="primary"
              icon={<Save size={18} />}
              onClick={handleSaveClick}
              loading={loading}
            >
              {note ? '수정' : '저장'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default SecureNoteForm;